import { TabSetting, TabViewer } from "../../../../../../../react-components/lu-component/src/index";
import * as React from "react";
import { TestOnlyConceptKey } from "../../../../../../../server/models/concept";
import { GetSimulationV3Response } from "../../../../../../../server/types/request/report/simulation";
import styled from "styled-components";
import { CorrectionValue } from "../../../../../../../server/models/simulation-value";
import { AccountWithoutPassword } from "../../../../../../../server/models/account";
import { ReportBigTitle } from "../../parts/report-title";
import { CPPage, CPPageProps } from "./cp";
import { Role } from "../../../../../../../server/lib/permission/role";
import { getAnalyticsVersion } from "../../../../../lib/action-standard";
import { ErrorObject } from "../../../../../../../server/types/error";
import { SimulationOne } from "./simulation-one";

const AreaWrap = styled.div`
	width: 1500px;
	margin: 0px auto;
	padding-left: 5px;
`;
const AlertRed = styled.span`
	display: inline-block;
	color: red;
	font-size: 12px;
`;

export type SimulationPageProps = GetSimulationV3Response & {
	errors?: ErrorObject;
	loginAccount: AccountWithoutPassword;
	onChangeRatio: <K extends keyof CorrectionValue>(
		target: TestOnlyConceptKey,
		name: K,
		value: CorrectionValue[K],
	) => void;
	// simulation
	onSimulation: (conceptType: "test1" | "test2") => void;
	onSimulationSave: (conceptType: "test1" | "test2") => void;
	// cp
	onChangeCP: CPPageProps["onChange"];
	onSubmitRecognitionRate: CPPageProps["onSubmitRecognitionRate"];
	onSubmitPurchaseRate: CPPageProps["onSubmitPurchaseRate"];
	confirmMessage: string | undefined;
	setChanged: (changed: boolean) => void;
	onClearCP: () => void;
	refetch: () => void;
};

export const SimulationLatestPage = React.memo((props: SimulationPageProps) => {
	const {
		loginAccount,
		onChangeRatio,
		goalSalesVolume,
		job,
		activity,
		oneNumberScoreNorm,
		oneNumberScoreData,
		onSimulation,
		onSimulationSave,
		evaluations,
		maxRecognition,
		onChangeCP,
		onSubmitRecognitionRate,
		onClearCP,
		onSubmitPurchaseRate,
		errors,
		setChanged,
		confirmMessage,
		currentProduct,
		simulationPredictionData,
		refetch,
		referenceSourceData,
		constant,
		goalRecognitionRate,
	} = props;
	const { test1, test2 } = simulationPredictionData;
	const hasEditRole = React.useMemo(
		() =>
			// 管理者 or ブランドメンバー or ブランドマネージャーは対象
			Role.isAdmin(loginAccount) ||
			Role.isActivityMember(loginAccount, activity) ||
			Role.isActivityBrandManager(loginAccount, activity),
		[activity, loginAccount],
	);
	const canChangeCP = React.useMemo(() => {
		if (job == null) return false;
		const version = getAnalyticsVersion(job.endDatetime);
		if (version === "v1" || version === "v2" || version === "v3") return false;
		// 現行品がない場合は、補正不可能
		if (currentProduct == null) return false;
		return true;
	}, [job, currentProduct]);
	const tabs = React.useMemo<TabSetting[]>(() => {
		const tmp: TabSetting[] = [];
		// 現行品がある場合のみ設定可能

		if (test1) {
			tmp.push({
				key: "test1",
				title: "テスト品1",
				children: (
					<SimulationOne
						hasEditRole={hasEditRole}
						evaluations={evaluations}
						target={"test1"}
						job={job}
						activity={activity}
						data={test1}
						goalSalesVolume={goalSalesVolume}
						constValue={constant}
						referenceSourceData={referenceSourceData}
						oneNumberScoreNorm={oneNumberScoreNorm}
						oneNumberScoreData={oneNumberScoreData}
						onSimulation={() => onSimulation("test1")}
						onSimulationSave={() => onSimulationSave("test1")}
						maxRecognition={maxRecognition}
						onChangeRatio={(name, value) => onChangeRatio("test1", name, value)}
						currentProduct={currentProduct}
						goalRecognitionRate={goalRecognitionRate}
					/>
				),
			});
		}
		if (test2) {
			tmp.push({
				key: "test2",
				title: "テスト品2",
				children: (
					<SimulationOne
						hasEditRole={hasEditRole}
						evaluations={evaluations}
						target={"test2"}
						job={job}
						activity={activity}
						data={test2}
						referenceSourceData={referenceSourceData}
						goalSalesVolume={goalSalesVolume}
						constValue={constant}
						oneNumberScoreNorm={oneNumberScoreNorm}
						oneNumberScoreData={oneNumberScoreData}
						onSimulation={() => onSimulation("test2")}
						onSimulationSave={() => onSimulationSave("test2")}
						maxRecognition={maxRecognition}
						onChangeRatio={(name, value) => onChangeRatio("test2", name, value)}
						currentProduct={currentProduct}
						goalRecognitionRate={goalRecognitionRate}
					/>
				),
			});
		}
		if (currentProduct && canChangeCP) {
			tmp.push({
				key: "cp",
				title: "実績値修正",
				children: (
					<CPPage
						currentProduct={currentProduct}
						hasEditRole={hasEditRole}
						constValue={constant}
						referenceSourceData={referenceSourceData}
						data={simulationPredictionData}
						job={job}
						onChange={onChangeCP}
						onSubmitPurchaseRate={onSubmitPurchaseRate}
						onSubmitRecognitionRate={onSubmitRecognitionRate}
						errors={errors}
						onClear={onClearCP}
						maxRecognition={maxRecognition}
						goalRecognitionRate={goalRecognitionRate}
					/>
				),
			});
		}
		return tmp;
	}, [
		test1,
		test2,
		currentProduct,
		canChangeCP,
		hasEditRole,
		evaluations,
		job,
		activity,
		goalSalesVolume,
		constant,
		referenceSourceData,
		oneNumberScoreNorm,
		oneNumberScoreData,
		maxRecognition,
		goalRecognitionRate,
		onSimulation,
		onSimulationSave,
		onChangeRatio,
		simulationPredictionData,
		onChangeCP,
		onSubmitPurchaseRate,
		onSubmitRecognitionRate,
		errors,
		onClearCP,
	]);
	return (
		<AreaWrap>
			<ReportBigTitle>
				補整＆提案フォーマット<AlertRed>（補整値は、アクティビテイメンバーと承認者のみ入力可能です）</AlertRed>
				{job ? `（${job.jobNum}）` : ""}
			</ReportBigTitle>

			{tabs.length > 0 && (
				<TabViewer
					id={"resultByTarget"}
					tabs={tabs}
					confirm={{
						title: "保存確認",
						body: confirmMessage,
					}}
					changed={!!confirmMessage}
					onChangeTab={() => {
						setChanged(false);
						refetch();
					}}
				/>
			)}
		</AreaWrap>
	);
});
